/**
 * Created by Jie Gao on 2019-04-16.
 */

import {EventHandler} from "@nolimitcity/slot-game/bin/core/event/EventHandler";

require("@nolimitcity/slot-game/lib/gsap/src/umd/CustomBounce.js");
import {GameConfig} from "@nolimitcity/slot-game/bin/core/gameconfig/GameConfig";
import {ReelPartAnimator} from "@nolimitcity/slot-game/bin/core/reel/reel/ReelPartAnimator";
import {ReelSymbolName} from "@nolimitcity/slot-game/bin/core/reelsymbol/ReelSymbolName";
import {StateReelSymbol} from "@nolimitcity/slot-game/bin/core/reelsymbol/StateReelSymbol";
import {StageManager} from "@nolimitcity/slot-game/bin/core/stage/StageManager";
import {MathHelper} from "@nolimitcity/slot-game/bin/core/utils/MathHelper";
import {AvalancheReelPartAnimator} from "@nolimitcity/slot-game/bin/game/reel/avalanchereel/AvalancheReelPartAnimator";
import {Timeline, TweenLite, TimelineLite} from "gsap";
import {GGGameConfig} from "../GGGameConfig";
import {StageEvent} from "@nolimitcity/slot-game/bin/core/stage/event/StageEvent";
import {GGReelSymbolName} from "../symbols/GGReelSymbolName";
import {GGGameModuleConfig} from "../GGGameModuleConfig";
import {GGReelSymbol} from "../symbols/GGReelSymbol";
import {GGReelView} from "./GGReelView";
import {Reels} from "@nolimitcity/slot-game/bin/core/reel/reelarea/Reels";

export class GGReelPartAnimator extends AvalancheReelPartAnimator {

    private readonly STAGE_INIT_Y:number = 0;
    private _wasNearWinSymbol:boolean = false;
    private _nearWinBounce:TweenLite;

    public get targetPositions():number[] {
        return this._targetPositions;
    }

    constructor(reelId:number) {
        super(reelId);
        CustomBounce.create("dropBounce", {strength : 0.2, squash : 0, squashID : "drop"});
        CustomBounce.create("shakeBounce", {strength : 0.8, squash : 0, endAtStart : true, squashID : "shake"});
        EventHandler.addEventListener(this, StageEvent.STAGE_RESIZED, () => this.onStageResized());
    }

    private onStageResized():void {
        if(this._nearWinBounce && this._nearWinBounce.isActive()) {
            this._nearWinBounce.progress(1);
            StageManager.stage.y = this.STAGE_INIT_Y;
        }
    }

    protected initTargetPositions():void {
        this._targetPositions = [];
        let targetPosition:number;
        const positionsNumTotal:number = GameConfig.instance.SYMBOLS_NUM_IN_REEL[2];
        for(let i:number = 0; i < positionsNumTotal; i++) {
            targetPosition = this.symStep * (i - positionsNumTotal) + this.symHeight / 2;
            this._targetPositions.push(targetPosition);
        }
    }

    public resetSize(symHeight:number, updateSymbols:boolean):void {
        this.symHeight = symHeight;
        this.initTargetPositions();
        if(updateSymbols) {
            this.initSymbolsPosition();
            for(var i = 0; i < this._symbolsToAnimate.length; i++) {
                this.symbolsToAnimate[i].symHeight = symHeight;
            }
        }
    }

    public resetSizeAfterBomb(symHeight:number, bombArea:boolean[]):void {
        this.symHeight = symHeight;
        this.initTargetPositions();

        for(var i = 0; i < this._symbolsToAnimate.length; i++) {
            if(!bombArea[i]) {
                (<GGReelView>Reels.getReel(this._reelId)!.view).addSymbolToBaseContainer(this.symbolsToAnimate[i]);
                this.symbolsToAnimate[i].y = this._targetPositions[i];
                this.symbolsToAnimate[i].symHeight = symHeight;
            }
        }

    }

    public resetStartSpin(isFastSpin:boolean):void {
        super.resetStartSpin(isFastSpin);
        this._wasNearWinSymbol = false;
    }

    protected addSymbolInAnimationToTimeline(symbol:StateReelSymbol, timeline:Timeline, delay:number, targetPosition:number, firstSymbol:boolean, nearWin:boolean, triggerAttention:boolean):number {
        if(symbol.symName.indexOf("X") > -1) {
            return delay;
        }

        if(symbol.stackedSymName!.isStacked && !symbol.visible) {
            return delay; // do not play invisible stacked symbols.
        }

        if(symbol.y === targetPosition) {
            return delay;
        }

        firstSymbol = (delay === 0);
        const distanceFactor:number = MathHelper.roundToDecimals((targetPosition - symbol.y) / GameConfig.instance.REEL_AREA_HEIGHT, 3);
        const isScatterAnticipation:boolean = GGGameModuleConfig.scatterAttention.allScatters.length < 4 && GGGameModuleConfig.scatterAttention.allScatters.length > 1;
        const isDeadWildAnticipation:boolean = GGGameModuleConfig.deadWildAttention.allDeadWilds.length > 2;
        nearWin = (nearWin && (isDeadWildAnticipation || isScatterAnticipation));

        // #529, turn off scatter anticipation for last reel if deadWilds are not anticipating.
        if(nearWin && this._reelId === 5 && (isScatterAnticipation && !isDeadWildAnticipation)) {
            nearWin = false;
        }
        //delay += ((nearWin || this._wasNearWinSymbol) && !this.isQuickStop) ? AvalancheReelPartAnimator._nearWinStopSymbolDelay[this._reelId] : this.stopSymDelay;
        delay += this.stopSymDelay;
        // Avalanche symbol should not change state.
        if(symbol.currentState!.keyword === GGGameConfig.instance.SYMBOL_STATES.spin.keyword) {
            const unBlurSymbolTimeline:TimelineLite = new TimelineLite({paused : true});
            unBlurSymbolTimeline.add(symbol.changeState({
                state : GameConfig.instance.SYMBOL_STATES.normal,
                replay : false,
                setStack : true,
                fadeInDuration : 0,
                fadeInDelay : 0
            }));
            timeline.add(() => unBlurSymbolTimeline.play(), delay);
            timeline.add(() => {}, delay + unBlurSymbolTimeline.totalDuration());
        }
        // #391

        const stopSpeed:number = (nearWin && !this.isQuickStop) ? ReelPartAnimator._nearWinSpinSpeeds[this._reelId] : this.stopSpeed;
        const wasNearWin:boolean = this._wasNearWinSymbol;
        let shakeOffset:number = 35;
        timeline.add(TweenLite.to(symbol, stopSpeed * distanceFactor, { // I don't abstract the time out here because we should override the whole animation function.
            ease : "dropBounce",
            y : targetPosition,
            onComplete : () => {
                if(nearWin && !this.isQuickStop) { // #391 #521
                    if(firstSymbol) {
                        GGGameModuleConfig.soundPlayer.playNearWinLandingSound(((0 - targetPosition) + symbol.symHeight * 0.5 - GGGameConfig.instance.REAL_REEL_AREA_HEIGHT) >= -10);
                    }
                    TweenLite.killTweensOf(StageManager.stage);
                    StageManager.stage.y = this.STAGE_INIT_Y;
                    this._nearWinBounce = TweenLite.to(StageManager.stage, 0.2, {
                        y : "+=" + shakeOffset,
                        ease : "shakeBounce"
                    });
                } else if(firstSymbol || wasNearWin) {
                    GGGameModuleConfig.soundPlayer.playLandingSound(this._reelId);
                    (<GGReelSymbol>symbol).playLandingSmoke();
                }
                if(triggerAttention) {
                    this.onSymbolInAttentionPointReached(symbol);
                }
            }
        }), delay);

        this._wasNearWinSymbol = nearWin && (symbol.symName === ReelSymbolName.SCATTER || symbol.symName === GGReelSymbolName.DEADWILD);

        return delay;
    }

    public ggGetNewSymbolsInAnimation(newSymbols:StateReelSymbol[], nearWinPositions:boolean[]):Timeline {
        // No win on this reel
        if(newSymbols.length === 0) {
            return new TimelineLite();
        }

        if(newSymbols.length + this.symbolsToAnimate.length != this.targetPositions.length) {
            debugger;
            throw new Error("AvalancheReelPartAnimator.getAvalancheNewAnimation(): the amount of symbols doesn't match the positions!");
        }

        const timeline:TimelineLite = new TimelineLite();
        let delay:number = 0;
        let firstSymbol:boolean = true;
        while(newSymbols.length > 0) {
            const symbol:StateReelSymbol = newSymbols.pop()!;
            this._symbolsToAnimate.unshift(symbol);
            const symId:number = newSymbols.length;
            delay = this.addSymbolInAnimationToTimeline(symbol, timeline, delay, this._targetPositions[symId], firstSymbol, nearWinPositions[symId], true);
            firstSymbol = false;
        }

        timeline.add(() => this._currentTimeline = null);
        this._currentTimeline = timeline;
        return timeline;
    }

    protected addSymbolOutAnimationToTimeline(symbol:StateReelSymbol, timeline:Timeline, delay:number, onSymbolCompleteCallback:(symbol:StateReelSymbol) => void):number {
        if(symbol.symName.indexOf("X") > -1) {
            onSymbolCompleteCallback(symbol);
        } else if(symbol.stackedSymName!.isStacked && !symbol.visible) {
            onSymbolCompleteCallback(symbol);
        } else {
            (<GGReelView>Reels.getReel(this._reelId)!.view).addSymbolToBaseContainer(symbol);
            delay += this.startSymDelay;
            timeline.add(TweenLite.to(symbol, this.startSpeed, { // I don't abstract the time out here because we should override the whole animation function.
                ease : GameConfig.instance.REEL_SPIN_START_EASE,
                y : symbol.y + GameConfig.instance.REEL_AREA_HEIGHT,
                onComplete : () => onSymbolCompleteCallback(symbol)
            }), delay);
        }
        return delay;
    }
}